import '../WidgetStyles.css';
import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import _ from 'lodash';
import { SizeLocationData } from 'builder/components/WidgetSizeLocation';
import { useDispatch } from 'react-redux';
// IMPORTANT: Use the default export (tracked + connected container) so the tracking prop is injected.
import { WidgetWrapper } from '../WidgetWrapper';
import {
  duplicateWidgetWithChildren,
  handleDownloadWidget,
  saveWidgetToMyWidgets,
} from '../WidgetWrapper/WidgetWrapper';
import { getWidget } from 'shared/widgets';
import { User } from 'oidc-client';
import uuidv4 from 'uuid/v4';
import PageLayout from '../../../../client/components/PageLayout';
import {
  processWidgetData,
  processRepeaterWidgetData,
} from '../../../../client/components/PageLayout/utils';
import {
  FOOTER_SECTION_ID,
  HEADER_SECTION_ID,
  widgetTypes,
} from '../../../util/constants';
import { ContextMenu } from '../ContextMenu/ContextMenu';
import { useHistory } from 'react-router-dom';
export interface WidgetConnectorNewProps<T> {
  currentPage: PageVersion;
  activeWidgetId: string;
  widgetState: Widget<T>;
  widgetId?: string;
  pages?: Page[];

  // Optional properties
  editing?: boolean;
  openPopup?: (open: boolean) => void;
  updateSizeLocation?: SizeLocationData;
  onUpdateConfig?: (
    config: any,
    widgetId: string,
    defaultWidget: boolean
  ) => void;
  updateCurrentPageWidgetConfig?: (config: any, widgetId: string) => void;
  getActiveWidgetId?: (widgetId: string) => void;
  updateCurrentPage?: (pageVersion: PageVersion) => void;
  updatePageDefaultWidget?: (
    widgetId: string,
    config: any,
    xDifference: number,
    yDifference: number
  ) => void;
  removeWidget?: (widgetId: string) => void;
  removeDefaultWidgets?: (widgetId: string) => void;
  addSibling?: (
    type: string,
    config: T,
    siblingWidgetId: string,
    children: string[],
    newWidgetId: string,
    tabIndex: number
  ) => void;
  closeConfigPanel?: (open: boolean) => void;
  shouldDisplayGridLines?: boolean;
  addTodoState?: (config: any) => void;
  updatePageContent?: (page: any) => void;
  undoState?: any;
  isUndoClick?: boolean;
  isRedoClick?: boolean;
  isResetState?: boolean;
  resetUndoRedo?: () => void;
  setResetToFalse?: () => void;
  page?: Page;
  onFlush?: () => void;
  updateWidgetPosition?: (
    bannerWidgetId: string,
    height: number,
    oldHeight: number
  ) => void;
  user?: User;
  siteId?: number;
  site?: Site;
  isAccordionPanelOpen: boolean;
  accordionShiftWidgetId: string;
  accordionHeightShift: number;
  initiateLogout?: () => void;
  normalizeHeaderFooter?: () => void;
  isEditingTemplate?: boolean;
  toggleSelectedIds?: (
    event: React.MouseEvent<HTMLDivElement>,
    id: string
  ) => void;
  addChild?: (
    type: string,
    initialConfig: T,
    location: number,
    widgetId: string,
    newParentWidgetId: string,
    tabIndex: number
  ) => void;
}

interface StickyBannerEditLayerProps {
  editing?: boolean;
  currentPage: PageVersion;
  stickyBannerWidget: Widget<any>;
  stickyBannerChildrenIds: Set<string>;
  stickyBannerIsFullScreen: boolean;
  handleUpdateConfig: (
    config: any,
    widgetId: string,
    defaultWidget: boolean
  ) => void;
  getActiveWidgetId?: (widgetId: string) => void;
  openPopup?: (open: boolean) => void;
  closeConfigPanel?: (open: boolean) => void;
  removeWidget?: (widgetId: string) => void;
  addChild?: (
    type: string,
    initialConfig: any,
    location: number,
    widgetId: string,
    newParentWidgetId: string,
    tabIndex: number
  ) => void;
  setResetToFalse?: () => void;
  siteId?: number;
  pages?: Page[];
  user?: User;
  initiateLogout?: () => void;
  site?: Site;
}

interface StickySelectionOverlayProps {
  dataTestId: string;
  style: React.CSSProperties;
  onSelect: () => void;
  onOpenSettings: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

// Selection wrapper for sticky banner content in edit mode. A native <button>
// can't be used: banner children can be Buttons/Links/inputs, and nesting
// interactive elements is invalid HTML that browsers reparse, which breaks both
// the child interaction and these selection handlers.
const StickySelectionOverlay: React.FC<StickySelectionOverlayProps> = ({
  dataTestId,
  style,
  onSelect,
  onOpenSettings,
  onContextMenu,
  children,
}) => (
  <div
    role="button"
    tabIndex={0}
    data-test-id={dataTestId}
    style={style}
    onClick={onSelect}
    onDoubleClick={() => {
      onSelect();
      onOpenSettings();
    }}
    onContextMenu={onContextMenu}
    onKeyDown={e => {
      if (e.key === 'Enter') {
        onSelect();
        onOpenSettings();
      } else if (e.key === ' ') {
        e.preventDefault();
        onSelect();
      }
    }}
  >
    {children}
  </div>
);

const StickyBannerEditLayer: React.FC<StickyBannerEditLayerProps> = ({
  editing,
  currentPage,
  stickyBannerWidget,
  stickyBannerChildrenIds,
  stickyBannerIsFullScreen,
  handleUpdateConfig,
  getActiveWidgetId,
  openPopup,
  closeConfigPanel,
  removeWidget,
  addChild,
  setResetToFalse,
  siteId,
  pages,
  user,
  initiateLogout,
  site,
}) => {
  const dispatch = useDispatch();
  const [stickyContextMenuWidgetId, setStickyContextMenuWidgetId] = useState<
    string | null
  >(null);
  const [stickyContextMenuPosition, setStickyContextMenuPosition] = useState({
    x: 0,
    y: 0,
  });
  const stickyContextMenuRef = useRef<HTMLDivElement>(null);
  const [canvasRect, setCanvasRect] = useState<{ left: number; width: number }>(
    { left: 0, width: 1280 }
  );

  useEffect(() => {
    if (!stickyContextMenuWidgetId) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        stickyContextMenuRef.current &&
        !stickyContextMenuRef.current.contains(e.target as Node)
      ) {
        setStickyContextMenuWidgetId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [stickyContextMenuWidgetId]);

  useEffect(() => {
    if (!editing) return;
    const updateCanvasRect = () => {
      const el = document.getElementById('editingContainerId');
      if (el) {
        const rect = el.getBoundingClientRect();
        setCanvasRect({ left: rect.left, width: rect.width });
      }
    };
    updateCanvasRect();
    window.addEventListener('resize', updateCanvasRect);
    return () => window.removeEventListener('resize', updateCanvasRect);
  }, [editing, stickyBannerWidget.id]);

  const handleStickyContextMenu = (e: React.MouseEvent, widgetId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const container = document.getElementById('editingContainerId');
    const containerRect = container?.getBoundingClientRect();
    const x = containerRect ? e.clientX - containerRect.left : e.clientX;
    const y = containerRect ? e.clientY - containerRect.top : e.clientY;
    setStickyContextMenuWidgetId(widgetId);
    setStickyContextMenuPosition({ x, y });
  };

  const isStickyContextActionDisabled = (widgetId: string) => {
    const widget = currentPage?.content?.[widgetId];
    if (!widget) return false;
    return (
      widget.config?.action === 'submit' ||
      [HEADER_SECTION_ID, FOOTER_SECTION_ID, 'loginButton'].includes(widget.id)
    );
  };

  const handleStickyDeleteWidget = () => {
    if (!stickyContextMenuWidgetId) return;
    removeWidget?.(stickyContextMenuWidgetId);
    closeConfigPanel?.(false);
    setStickyContextMenuWidgetId(null);
  };

  const handleStickyDuplicateWidget = () => {
    if (!stickyContextMenuWidgetId || !addChild || !currentPage) return;
    const widgetToDuplicate = currentPage.content[stickyContextMenuWidgetId];
    if (!widgetToDuplicate) return;

    if (widgetToDuplicate.children?.length > 0) {
      duplicateWidgetWithChildren({
        widgetToDuplicate,
        currentPage,
        addChild,
        parentWidgetId: widgetToDuplicate.config.parentId,
      });
    } else {
      const unique = uuidv4();
      const widgetConfig = {
        ...widgetToDuplicate.config,
        xLocation: widgetToDuplicate.config.xLocation + 10,
        yLocation: widgetToDuplicate.config.yLocation + 10,
        isWidgetDuplicated: true,
        parentId: widgetToDuplicate.config.parentId,
      };
      addChild(
        widgetToDuplicate.type,
        widgetConfig,
        0,
        unique,
        widgetToDuplicate.config.parentId,
        widgetToDuplicate.tabIndex ?? -1
      );
    }

    closeConfigPanel?.(false);
    setResetToFalse?.();
    setStickyContextMenuWidgetId(null);
  };

  const handleStickyDownloadWidget = () => {
    if (!stickyContextMenuWidgetId || !currentPage) return;
    const widgetState = currentPage.content[stickyContextMenuWidgetId];
    if (!widgetState) return;
    handleDownloadWidget(widgetState, currentPage);
    setStickyContextMenuWidgetId(null);
  };

  const handleStickySaveToMyWidgets = () => {
    if (!stickyContextMenuWidgetId || !currentPage || !siteId || !site) return;
    const widgetState = currentPage.content[stickyContextMenuWidgetId];
    if (!widgetState) return;
    saveWidgetToMyWidgets({
      widgetState,
      currentPage,
      siteId,
      site,
      dispatch,
    });
    setStickyContextMenuWidgetId(null);
  };

  if (!editing || typeof document === 'undefined') {
    return null;
  }

  return (
    <>
      {createPortal(
        <div
          data-test-id="sticky-banner-children-edit-container"
          style={{
            position: 'fixed',
            bottom: 0,
            left: stickyBannerIsFullScreen ? '0px' : `${canvasRect.left}px`,
            width: stickyBannerIsFullScreen ? '100vw' : `${canvasRect.width}px`,
            height: `${(stickyBannerWidget as any).config.height}px`,
            maxHeight: '50vh',
            overflowY: 'auto',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          <StickySelectionOverlay
            dataTestId="sticky-banner-select-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              pointerEvents: 'auto',
            }}
            onSelect={() => getActiveWidgetId?.((stickyBannerWidget as any).id)}
            onOpenSettings={() => openPopup?.(true)}
            onContextMenu={e =>
              handleStickyContextMenu(e, (stickyBannerWidget as any).id)
            }
          >
            {(() => {
              const BannerComponent: any = getWidget(
                (stickyBannerWidget as any).type,
                true
              ).components.render;
              return (
                <BannerComponent
                  currentPage={currentPage}
                  widgetState={stickyBannerWidget as any}
                  onChange={handleUpdateConfig}
                  value={(stickyBannerWidget as any).config}
                  editing={editing}
                  siteId={siteId}
                  pages={pages}
                  user={user}
                  initiateLogout={initiateLogout}
                  site={site}
                />
              );
            })()}
          </StickySelectionOverlay>
          <div
            style={{
              position: 'relative',
              maxWidth: '1280px',
              margin: '0 auto',
              height: '100%',
            }}
          >
            {Array.from(stickyBannerChildrenIds).map(childId => {
              const childWidget = currentPage?.content[childId];
              if (!childWidget) return null;
              const ChildComponent: any = getWidget(childWidget.type, true)
                .components.render;
              return (
                <StickySelectionOverlay
                  key={childId}
                  dataTestId={`sticky-banner-child-${childId}`}
                  style={{
                    position: 'absolute',
                    top: `${childWidget.config.yLocation -
                      (stickyBannerWidget as any).config.yLocation}px`,
                    left: `${(childWidget.config.xLocation / 1280) * 100}%`,
                    width: `${(childWidget.config.width / 1280) * 100}%`,
                    height: `${childWidget.config.height}px`,
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                  }}
                  onSelect={() => getActiveWidgetId?.(childId)}
                  onOpenSettings={() => openPopup?.(true)}
                  onContextMenu={e => handleStickyContextMenu(e, childId)}
                >
                  <ChildComponent
                    currentPage={currentPage}
                    widgetState={childWidget}
                    onChange={handleUpdateConfig}
                    value={childWidget.config}
                    editing={editing}
                    siteId={siteId}
                    pages={pages}
                    user={user}
                    initiateLogout={initiateLogout}
                    site={site}
                  />
                </StickySelectionOverlay>
              );
            })}
          </div>
        </div>,
        document.body
      )}

      {stickyContextMenuWidgetId && (
        <ContextMenu
          ref={stickyContextMenuRef}
          disabled={isStickyContextActionDisabled(stickyContextMenuWidgetId)}
          openUpwards={true}
          position={stickyContextMenuPosition}
          menuitems={[
            {
              label: 'View Settings',
              action: () => {
                getActiveWidgetId?.(stickyContextMenuWidgetId);
                openPopup?.(true);
                setStickyContextMenuWidgetId(null);
              },
            },
            {
              label: 'Delete',
              action: handleStickyDeleteWidget,
              shortcut: '\u2318 + ⌫',
            },
            {
              label: 'Duplicate',
              action: handleStickyDuplicateWidget,
              shortcut: '\u2318 + D',
            },
            {
              label: 'Download',
              action: handleStickyDownloadWidget,
            },
            {
              label: 'Save to My Widgets',
              action: handleStickySaveToMyWidgets,
            },
          ]}
        />
      )}
    </>
  );
};

export const WidgetConnectorNew: React.FC<WidgetConnectorNewProps<any>> = (
  props: WidgetConnectorNewProps<any> & { tracking?: any }
) => {
  const {
    currentPage,
    openPopup,
    onUpdateConfig,
    getActiveWidgetId,
    activeWidgetId,
    updateCurrentPage,
    removeWidget,
    addSibling,
    closeConfigPanel,
    shouldDisplayGridLines,
    editing,
    undoState,
    addTodoState,
    page,
    isUndoClick,
    isRedoClick,
    isResetState,
    setResetToFalse,
    onFlush,
    updateSizeLocation,
    pages,
    updatePageDefaultWidget,
    updateCurrentPageWidgetConfig,
    removeDefaultWidgets,
    user,
    initiateLogout,
    normalizeHeaderFooter,
    siteId,
    site,
    addChild,
    tracking,
  } = props;

  const prevPropsRef = useRef<WidgetConnectorNewProps<any>>(props);

  useEffect(() => {
    const prevProps = prevPropsRef.current;

    if (!_.isEqual(page?.current.content, prevProps?.page?.current.content)) {
      if (!isRedoClick && !isUndoClick && !isResetState) {
        addTodoState(page);
      }
      if (prevProps?.page?.id && prevProps.page.id !== page?.id) {
        onFlush();
      }
      prevPropsRef.current = props;
    }
  }, [
    page,
    activeWidgetId,
    isRedoClick,
    isUndoClick,
    isResetState,
    addTodoState,
    updateSizeLocation,
    currentPage,
  ]);

  // const dispatch = useDispatch()

  useEffect(() => {
    if (isRedoClick || isUndoClick) {
      props.updatePageContent(undoState);
      props.resetUndoRedo();
    }
  }, [
    isRedoClick,
    isUndoClick,
    undoState,
    props.updatePageContent,
    props.resetUndoRedo,
  ]);

  useEffect(() => {
    if (!currentPage) return;

    const repeaterWidgets = Object.values(currentPage.content).filter(
      widget => widget?.type === widgetTypes.RepeaterWidget
    );

    if (repeaterWidgets.length === 0) return;

    let hasChanges = false;
    const updatedPage = { ...currentPage };

    repeaterWidgets.forEach(repeaterWidget => {
      const repeaterData = repeaterWidget.config?.repeaterData;
      const containers = repeaterWidget.children;

      if (!repeaterData || !Array.isArray(repeaterData.data)) return;

      containers.forEach(containerId => {
        const container = currentPage.content[containerId];

        if (container?.config?.visible === false) return;

        const dataMapping = repeaterWidget.config?.repeaterDataMapping || {};
        const rowIndex = dataMapping[containerId];
        const rowData = repeaterData?.data?.[rowIndex];

        if (rowIndex == null || !container.children || !rowData) return;

        container.children.forEach(widgetId => {
          const widget = currentPage.content[widgetId];
          const key = widget.config?.repeaterWidgetKey;

          if (!key || !(key in rowData)) return;

          const value = rowData[key];

          const currentValue =
            widget.type === widgetTypes.Text
              ? widget.config.text
              : widget.type === widgetTypes.Link
              ? widget.config.displayText
              : widget.type === widgetTypes.Image
              ? widget.config.backgroundImage?.url
              : widget.config.displayText;

          if (currentValue !== value) {
            const newConfig = { ...widget.config };

            if (widget.type === widgetTypes.Text) {
              newConfig.text = value;
            } else if (widget.type === widgetTypes.Link) {
              newConfig.displayText = value;
            } else if (widget.type === widgetTypes.Image) {
              newConfig.backgroundImage = {
                ...newConfig.backgroundImage,
                url: value,
              };
            } else {
              newConfig.displayText = value;
            }

            updatedPage.content[widgetId] = {
              ...widget,
              config: newConfig,
            };
            hasChanges = true;
          }
        });
      });
    });

    if (hasChanges && typeof updateCurrentPage === 'function') {
      updateCurrentPage(updatedPage);
    }
  }, [currentPage, updateCurrentPage]);

  const history = useHistory();

  // Memoize the tracked container (which provides the tracking prop) instead of raw WidgetWrapper.
  const ChildMemo = React.memo(WidgetWrapper, (prevProps, nextProps) => {
    return _.isEqual(prevProps, nextProps);
  });

  const handleUpdateConfig = (
    config: any,
    widgetId: string,
    defaultWidget: boolean
  ) => {
    onUpdateConfig(config, widgetId, defaultWidget);
    setResetToFalse();
  };

  if (!undoState) {
    addTodoState(page);
  }

  //preview before
  if (currentPage && !editing) {
    // Create a component that can re-render when filters change
    const ClientModeRenderer = () => {
      const [filterChangeCounter, setFilterChangeCounter] = React.useState(0);

      React.useEffect(() => {
        const handleFilterChange = () => {
          setFilterChangeCounter(prev => {
            const newValue = prev + 1;
            return newValue;
          });
        };

        window.addEventListener('repeaterFilterChange', handleFilterChange);

        return () => {
          window.removeEventListener(
            'repeaterFilterChange',
            handleFilterChange
          );
        };
      }, []);

      // Reprocess widgets whenever filters change
      const processedData = React.useMemo(() => {
        const updatedPage = processRepeaterWidgetData(currentPage);
        const widgets = processWidgetData(
          updatedPage,
          handleUpdateConfig,
          user,
          pages,
          siteId,
          site,
          initiateLogout,
          history
        );

        return {
          widgets,
          updatedPage,
        };
      }, [filterChangeCounter]);

      return (
        <PageLayout
          initialWidgets={processedData.widgets}
          currentPage={processedData.updatedPage}
          site={site}
          data-testid="page-layout"
        />
      );
    };

    return <ClientModeRenderer />;
  }

  // Identify sticky-bottom banner for edit mode so its children can be
  // co-rendered inside the same fixed-bottom container as the banner.
  const stickyBannerWidget = currentPage
    ? Object.values(currentPage.content).find(
        (w: any) =>
          w &&
          w.type === widgetTypes.Banner &&
          w.config?.placement === 'Sticky Bottom (Fixed to Screen)'
      )
    : undefined;
  const stickyBannerChildrenIds = new Set<string>(
    stickyBannerWidget?.children ?? []
  );
  const stickyBannerIsFullScreen = !!stickyBannerWidget?.config
    ?.enableFullScreen;
  const footerHeight =
    currentPage?.content?.[FOOTER_SECTION_ID]?.config?.height;
  const stickyBannerBottomSpace = stickyBannerWidget
    ? Math.max(
        (stickyBannerWidget as any).config.height || 0,
        typeof footerHeight === 'number' ? footerHeight : 0
      )
    : 0;

  return (
    <>
      <div
        className="widgetConnectorNewDiv"
        id="widgetConnectorNewId"
        style={
          stickyBannerWidget
            ? {
                paddingBottom: `${stickyBannerBottomSpace}px`,
              }
            : undefined
        }
      >
        {currentPage &&
          editing &&
          Object.keys(currentPage.content).map(item => {
            // Skip the sticky banner itself and its children — they are
            // grouped and rendered together in the fixed-bottom container
            // below so the whole group moves to the viewport bottom as one.
            if (
              (stickyBannerWidget && item === (stickyBannerWidget as any).id) ||
              stickyBannerChildrenIds.has(item)
            )
              return null;

            if (
              currentPage.content[item]?.config?.isHorizonWidget === true &&
              (currentPage.content[item]?.config?.visible === true ||
                currentPage.content[item]?.config?.visible === undefined)
            ) {
              const WidgetComponent = getWidget(
                currentPage.content[item].type,
                true
              ).components.render;
              const RenderedComponent = (
                <WidgetComponent
                  currentPage={currentPage}
                  widgetState={currentPage.content[item]}
                  onChange={handleUpdateConfig}
                  value={currentPage.content[item].config}
                  editing={props.editing}
                  siteId={props.siteId}
                  pages={pages}
                  user={user}
                  initiateLogout={initiateLogout}
                  site={props.site}
                />
              );

              if (
                // This conditional ensures the RichTextEditor is not rendered inside the WidgetWrapper
                // while working in the RichTextEditor interface. This is necessary due to re-render issues caused by the
                // SiteBuilder editor which cause bugs and inconsistent behavior when interacting with the RichTextEditor.
                currentPage.content[item].type === widgetTypes.RichText &&
                currentPage.content[item].config.isEditingRichText
              ) {
                return <span key={item}>{RenderedComponent}</span>;
              }

              // All widgets including RepeaterWidget follow the same rendering path
              return (
                <ChildMemo
                  widgetId={item}
                  updateWidgetPosition={props.updateWidgetPosition}
                  locationCoords={{
                    x: currentPage.content[item].config.xLocation,
                    y:
                      currentPage.content[item].config.yLocation +
                      (currentPage.content[item].config.marginTop || 0),
                    width: currentPage.content[item].config.width,
                    height: currentPage.content[item].config.height,
                    minWidth: currentPage.content[item].config.minWidth,
                    minHeight: currentPage.content[item].config.minHeight,
                    widgetType: currentPage.content[item].type,
                    originalYLocation:
                      currentPage.content[item].config.originalYLocation,
                  }}
                  widgetState={currentPage.content[item]}
                  activeWidgetId={activeWidgetId}
                  key={item}
                  openPopup={openPopup}
                  component={RenderedComponent}
                  updateCurrentPageWidgetConfig={updateCurrentPageWidgetConfig}
                  removeDefaultWidgets={removeDefaultWidgets}
                  getActiveWidgetId={getActiveWidgetId}
                  currentPage={currentPage}
                  updateCurrentPage={updateCurrentPage}
                  removeWidget={removeWidget}
                  addSibling={addSibling}
                  closeConfigPanel={closeConfigPanel}
                  shouldDisplayGridLines={shouldDisplayGridLines}
                  editingMode={true}
                  setResetToFalse={setResetToFalse}
                  updatePageDefaultWidget={updatePageDefaultWidget}
                  user={user}
                  pages={pages}
                  isAccordionPanelOpen={props.isAccordionPanelOpen}
                  accordionShiftWidgetId={props.accordionShiftWidgetId}
                  accordionHeightShift={props.accordionHeightShift}
                  initiateLogout={initiateLogout}
                  normalizeHeaderFooter={normalizeHeaderFooter}
                  siteId={props.siteId}
                  site={site}
                  isEditingTemplate={props.isEditingTemplate}
                  addChild={addChild}
                  tracking={tracking}
                  data-testid="widget-wrapper"
                />
              );
            }
          })}
      </div>
      {editing && stickyBannerWidget && (
        <StickyBannerEditLayer
          editing={editing}
          currentPage={currentPage}
          stickyBannerWidget={stickyBannerWidget as Widget<any>}
          stickyBannerChildrenIds={stickyBannerChildrenIds}
          stickyBannerIsFullScreen={stickyBannerIsFullScreen}
          handleUpdateConfig={handleUpdateConfig}
          getActiveWidgetId={getActiveWidgetId}
          openPopup={openPopup}
          closeConfigPanel={closeConfigPanel}
          removeWidget={removeWidget}
          addChild={addChild}
          setResetToFalse={setResetToFalse}
          siteId={siteId}
          pages={pages}
          user={user}
          initiateLogout={initiateLogout}
          site={site}
        />
      )}
    </>
  );
};
